// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-7 — partner income inside Income & credits (owner, 2026-11-03): money for both partners (split straight away)
// or for one partner only. It is NOT revenue — no reserve, no costs — so it is kept apart from the lines above and
// lands in the partner balances (Partners section). Shows the entries dated in the selected month.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import { Plus } from 'lucide-react'
import AppButton from '../../../components/ui/AppButton'
import { showDate } from '../../../components/ui/DateInput'
import SummaryLines from '../../../components/ui/SummaryLines'
import { financeKeys, getPartners } from '../../../api/finance'
import { money } from './FinanceTables'
import { monthLabel } from './CostItemDialogs'
import { PARTNER_LABEL, PartnerEntryDialog } from './PartnersPanel'

export default function PartnerIncomeBlock({ month, devSplitPct }: { month: string; devSplitPct: number }) {
  const [open, setOpen] = useState(false)
  const q = useQuery({ queryKey: financeKeys.partners, queryFn: getPartners })
  const lines = (q.data?.entries ?? []).filter(e =>
    (e.kind === 'income_shared' || e.kind === 'income_direct') && !e.voided_at && e.entry_date.startsWith(month))
  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Box sx={{ fontWeight: 700, fontSize: 13 }}>Partner income — {monthLabel(month)}</Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            Not revenue: for both partners (split {devSplitPct}/{100 - devSplitPct} straight away) or for one partner only. It goes to the partner balances.
          </Box>
        </Box>
        <AppButton size="small" variant="contained" startIcon={<Plus size={14} />} onClick={() => setOpen(true)}>Add partner income</AppButton>
      </Box>
      {lines.length === 0
        ? <Box sx={{ fontSize: 12.5, color: 'text.secondary', py: 1 }}>{q.isLoading ? 'Loading…' : `No partner income dated in ${monthLabel(month)}.`}</Box>
        : <SummaryLines lines={lines.map(e => ({
            key: e.id,
            label: `${showDate(e.entry_date)} · ${e.title} — ${e.partner ? `${PARTNER_LABEL[e.partner]} only` : `both (Blue Dev ${money(e.blue_usd)} · Owl ${money(e.owl_usd)})`}`,
            value: money(e.amount_usd),
          }))} />}
      {open && <PartnerEntryDialog open mode="income" devSplitPct={devSplitPct} onClose={() => setOpen(false)} />}
    </Box>
  )
}
